import PropTypes from 'prop-types';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { TextField, InputAdornment, Pagination } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import rtlPlugin from 'stylis-plugin-rtl';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faInfoCircle, faFolderPlus } from '@fortawesome/free-solid-svg-icons';
import { FaFolder, FaFolderOpen } from 'react-icons/fa';
import { FileText, Image as ImageIcon, Video as VideoIcon, Presentation, Sheet } from 'lucide-react';
import { UserContext } from '../../context/UserContext';
import { permissions } from '../../utils/permissions-utils';
import CommonLayout from '../../components/CommonLayout';
import FloatingAddButton from '../../components/FloatingAddButton';
import DropdownWithIcon from '../../components/DropdownWithIcon';
import UploadErrors from '../../components/UploadErrors';
import DeleteConfirmationDialog from '../../components/dialogs/DeleteConfirmationDialog';
import departmentIcon from '../../assets/protocols/department.svg';
import useDocumentDepartments from './useDocumentDepartments';
import useDocumentLibrary from './useDocumentLibrary';
import NameDialog from './NameDialog';
import UploadDocumentsDialog from './UploadDocumentsDialog';
import DocumentDetailsDialog from './DocumentDetailsDialog';
import { viewerKindOf } from './documentTypes';
import { canDropInto, folderOptions, folderPath, folderPathLabel, ROOT_LABEL, tileDescription } from './libraryTree';
import * as api from './departmentDocumentsApi';
// The protocols list's look, reused as is.
import '../protocols/Protocols.scss';
import '../protocols/EntitiesList.scss';
import '../protocols/ProtocolSearchBox.scss';
import './DepartmentDocuments.scss';

const cacheRtl = createCache({
  key: 'muirtl',
  stylisPlugins: [prefixer, rtlPlugin],
});

const theme = createTheme({
  typography: { fontFamily: '"Rubik", sans-serif' },
  direction: 'rtl',
});

const LAST_DEPARTMENT_KEY = 'departmentDocuments.lastDepartment';

const readLastDepartment = () => {
  try { return localStorage.getItem(LAST_DEPARTMENT_KEY); } catch { return null; }
};
const writeLastDepartment = (id) => {
  try { localStorage.setItem(LAST_DEPARTMENT_KEY, id); } catch { /* private mode */ }
};

const DocumentIcon = ({ fileName }) => {
  const kind = viewerKindOf(fileName);
  const ext = (fileName || '').split('.').pop()?.toLowerCase();
  if (kind === 'image' || ext === 'tif' || ext === 'tiff') return <ImageIcon size={20} className="dd-item-icon" />;
  if (kind === 'video' || ext === 'avi') return <VideoIcon size={20} className="dd-item-icon" />;
  if (kind === 'pptx' || ext === 'ppt') return <Presentation size={20} className="dd-item-icon" />;
  if (kind === 'sheet') return <Sheet size={20} className="dd-item-icon" />;
  return <FileText size={20} className="dd-item-icon" />;
};

DocumentIcon.propTypes = { fileName: PropTypes.string };

// Row actions sit inside the row's <Link>, so each one stops the navigation.
// A real button (not a bare icon) keeps them reachable by keyboard and named
// for screen readers.
const ActionButton = ({ icon, label, onClick }) => (
  <button type="button" className="dd-action" aria-label={label} title={label} onClick={onClick}>
    <FontAwesomeIcon icon={icon} className="action-icon" />
  </button>
);

ActionButton.propTypes = {
  icon: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};

const SkeletonCard = () => (
  <div className="skeleton-protocol">
    <div className="skeleton-right"><div className="skeleton-title" /></div>
    <div className="skeleton-left"><div className="skeleton-icon" /></div>
  </div>
);

function DepartmentDocuments() {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const departments = useDocumentDepartments();

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const term = searchParams.get('q') || '';
  const page = Math.max(parseInt(searchParams.get('page'), 10) || 1, 1);

  // Department: the URL, then the last one used here, then the first available.
  const departmentId = useMemo(() => {
    const ids = departments.map(d => d.id);
    const fromUrl = searchParams.get('dep');
    if (fromUrl && ids.includes(fromUrl)) return fromUrl;
    const last = readLastDepartment();
    if (last && ids.includes(last)) return last;
    return ids[0] || null;
  }, [departments, searchParams]);

  useEffect(() => {
    if (departmentId) writeLastDepartment(departmentId);
  }, [departmentId]);

  const library = useDocumentLibrary({
    departmentId,
    folderId: searchParams.get('folder') || null,
    term,
    page,
  });
  const { allFolders, openFolderId, isLoading, error: loadError, reload } = library;

  const [uploadOpen, setUploadOpen] = useState(false);
  const [nameDialog, setNameDialog] = useState(null); // { mode: 'newFolder' | 'editFolder' | 'editDocument', target }
  const [detailsDoc, setDetailsDoc] = useState(null);
  const [toDelete, setToDelete] = useState(null); // { kind: 'folder' | 'document', item }
  const [deleteError, setDeleteError] = useState('');
  const [actionError, setActionError] = useState('');
  // Drag and drop: what is being dragged, and which drop target it is over.
  const [dragItem, setDragItem] = useState(null);
  const [dropTarget, setDropTarget] = useState(undefined);

  const canEdit = !!departmentId && permissions.hasPermission(user, departmentId, ['EDITOR', 'ADMIN']);

  const updateParams = (changes) => {
    const next = new URLSearchParams(location.search);
    Object.entries(changes).forEach(([key, value]) => {
      if (value) next.set(key, value);
      else next.delete(key);
    });
    navigate({ pathname: location.pathname, search: next.toString() }, { replace: true });
  };

  // Opening a folder leaves search and starts at its first page.
  const folderLink = (folderId) => {
    const next = new URLSearchParams();
    if (searchParams.get('dep')) next.set('dep', searchParams.get('dep'));
    if (folderId) next.set('folder', folderId);
    return `/department-documents${next.toString() ? `?${next}` : ''}`;
  };

  // A delete or a move can leave the URL on a page that no longer exists.
  useEffect(() => {
    if (!isLoading && page > 1 && library.pages < page) {
      updateParams({ page: library.pages > 1 ? String(library.pages) : null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, page, library.pages]);

  const isSearching = term.trim().length > 0;
  const breadcrumb = folderPath(allFolders, openFolderId);
  const openFolderDescription = breadcrumb[breadcrumb.length - 1]?.description?.trim();
  const destinationOptions = useMemo(() => folderOptions(allFolders), [allFolders]);

  // --- Actions -------------------------------------------------------------

  const handleNameSubmit = async ({ name, description, folder, documentDate, author }) => {
    const { mode, target } = nameDialog;
    if (mode === 'newFolder') {
      await api.createFolder({ department: departmentId, name, description, parent: folder });
    } else if (mode === 'editFolder') {
      await api.updateFolder(target._id, { name, description, parent: folder });
    } else {
      await api.updateDocument(target._id, { name, folder, documentDate, author });
    }
    setNameDialog(null);
    reload();
  };

  const handleDelete = async () => {
    try {
      if (toDelete.kind === 'folder') await api.deleteFolder(toDelete.item._id);
      else await api.deleteDocument(toDelete.item._id);
      setToDelete(null);
      reload();
    } catch (error) {
      setDeleteError(error?.serverError?.hebMessage || 'המחיקה נכשלה. אנא נסה שנית.');
    }
  };

  const moveInto = async (item, targetFolderId) => {
    setActionError('');
    try {
      if (item.kind === 'folder') await api.updateFolder(item.id, { parent: targetFolderId });
      else await api.updateDocument(item.id, { folder: targetFolderId });
      reload();
    } catch (error) {
      setActionError(error?.serverError?.hebMessage || 'ההעברה נכשלה. אנא נסה שנית.');
    }
  };

  const stop = (handler) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    handler();
  };

  // --- Drag and drop (editors only; on touch screens the edit dialog moves) --

  const dragSource = (item) => (canEdit ? {
    draggable: true,
    onDragStart: (e) => {
      e.dataTransfer.effectAllowed = 'move';
      // Firefox starts a drag only when some data is set.
      e.dataTransfer.setData('text/plain', item.id);
      setDragItem(item);
    },
    onDragEnd: () => {
      setDragItem(null);
      setDropTarget(undefined);
    },
  } : { draggable: false });

  const dropZone = (targetFolderId) => {
    if (!canEdit || !canDropInto(allFolders, dragItem, targetFolderId)) return {};
    return {
      onDragOver: (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dropTarget !== targetFolderId) setDropTarget(targetFolderId);
      },
      onDragLeave: (e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setDropTarget(undefined);
      },
      onDrop: (e) => {
        e.preventDefault();
        const item = dragItem;
        setDragItem(null);
        setDropTarget(undefined);
        moveInto(item, targetFolderId);
      },
    };
  };

  const dropClass = (targetFolderId) => (
    dragItem && dropTarget === targetFolderId ? ' dd-drop-target' : ''
  );

  // --- Render --------------------------------------------------------------

  // Folders are square tiles in a grid (like the home screen); documents are rows.
  const renderFolder = (folder) => (
    <Link
      key={`f-${folder._id}`}
      to={folderLink(folder._id)}
      className={`dd-folder-tile${dropClass(folder._id)}${dragItem?.id === folder._id ? ' dd-dragging' : ''}`}
      data-testid={`folder-${folder._id}`}
      {...dragSource({ kind: 'folder', id: folder._id, from: folder.parent || null })}
      {...dropZone(folder._id)}
    >
      {canEdit && (
        <div className="dd-folder-actions">
          <ActionButton icon={faEdit} label="עריכת תיקייה" onClick={stop(() => setNameDialog({ mode: 'editFolder', target: folder }))} />
          <ActionButton icon={faTrash} label="מחיקת תיקייה" onClick={stop(() => setToDelete({ kind: 'folder', item: folder }))} />
        </div>
      )}
      <FaFolder className="dd-folder-tile-icon" />
      <span className="dd-folder-name">{folder.name}</span>
      {folder.description && (
        <span className="dd-folder-description" title={folder.description}>{tileDescription(folder.description)}</span>
      )}
      {isSearching && <span className="dd-folder-description">{folderPathLabel(allFolders, folder.parent)}</span>}
    </Link>
  );

  const renderDocument = (doc) => (
    <Link
      key={`d-${doc._id}`}
      to={`/department-documents/${doc._id}`}
      className={`entity-item${dragItem?.id === doc._id ? ' dd-dragging' : ''}`}
      data-testid={`document-${doc._id}`}
      {...dragSource({ kind: 'document', id: doc._id, from: doc.folder || null })}
    >
      <div className="entity-content">
        <div className="entity-info">
          <span className="entity-name dd-name">
            <DocumentIcon fileName={doc.file?.originalName} />
            <span>
              {doc.name}
              {isSearching && <span className="dd-subtitle">{folderPathLabel(allFolders, doc.folder)}</span>}
            </span>
          </span>
          <div className="entity-actions">
            <ActionButton icon={faInfoCircle} label="פרטים" onClick={stop(() => setDetailsDoc(doc))} />
            {canEdit && (
              <>
                <ActionButton icon={faEdit} label="עריכת מסמך" onClick={stop(() => setNameDialog({ mode: 'editDocument', target: doc }))} />
                <ActionButton icon={faTrash} label="מחיקת מסמך" onClick={stop(() => setToDelete({ kind: 'document', item: doc }))} />
              </>
            )}
          </div>
        </div>
      </div>
    </Link>
  );

  const libraryEmpty = library.totalDocuments === 0 && allFolders.length === 0;
  const nothingVisible = library.folders.length === 0 && library.documents.length === 0;

  const nameDialogProps = {
    newFolder: {
      title: 'תיקייה חדשה',
      placeholder: 'שם התיקייה',
      submitText: 'יצירה',
      withDescription: true,
      folderOptions: destinationOptions,
      initialFolder: openFolderId,
    },
    editFolder: {
      title: 'עריכת תיקייה',
      placeholder: 'שם התיקייה',
      initialName: nameDialog?.target?.name,
      withDescription: true,
      initialDescription: nameDialog?.target?.description || '',
      folderOptions: nameDialog?.target ? folderOptions(allFolders, nameDialog.target._id) : undefined,
      initialFolder: nameDialog?.target?.parent || null,
    },
    editDocument: {
      title: 'עריכת מסמך',
      placeholder: 'שם המסמך',
      withDocumentDetails: true,
      initialDate: nameDialog?.target?.documentDate || '',
      initialAuthor: nameDialog?.target?.author || '',
      initialName: nameDialog?.target?.name,
      folderOptions: destinationOptions,
      initialFolder: nameDialog?.target?.folder || null,
    },
  }[nameDialog?.mode] || {};

  // The folder path doubles as a drop target: drag onto any folder above to move up.
  const pathLink = (folderId, label) => (
    <Link to={folderLink(folderId)} className={`dd-path-link${dropClass(folderId)}`} {...dropZone(folderId)}>
      {label}
    </Link>
  );

  return (
    <CommonLayout>
      <div className="protocols-container department-documents" style={{ position: 'relative', minHeight: '50dvh' }}>
        <CacheProvider value={cacheRtl}>
          <ThemeProvider theme={theme}>
            {departments.length > 1 && (
              <div className="dd-department-select">
                <DropdownWithIcon
                  icon={departmentIcon}
                  placeholder="מחלקה"
                  value={departmentId || ''}
                  onChange={(e) => updateParams({ dep: e.target.value, folder: null, q: null, page: null })}
                >
                  {departments.map(d => (
                    <option key={d.id} value={d.id}>{d.label}</option>
                  ))}
                </DropdownWithIcon>
              </div>
            )}

            {departments.length === 0 ? (
              <div className="dd-empty">
                <FaFolderOpen size={60} className="dd-empty-icon" />
                <p>
                  {permissions.isWaitingForApproval(user)
                    ? 'ברגע שהצטרפותך למחלקה תאושר, יהיה באפשרותך לצפות במסמכי המחלקה.'
                    : 'מסמכי המחלקה זמינים לחברי מחלקה בלבד.'}
                </p>
              </div>
            ) : (
              <>
                <div className="protocol-search-box">
                  <div className="search-container">
                    <TextField
                      className="search-input"
                      placeholder="חיפוש בכל המסמכים..."
                      value={term}
                      onChange={(e) => updateParams({ q: e.target.value, page: null })}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end"><SearchIcon /></InputAdornment>
                        ),
                      }}
                    />
                  </div>
                </div>

                <div className="dd-toolbar">
                  {isSearching ? (
                    <span className="dd-path-current">
                      תוצאות חיפוש בכל התיקיות
                      {!isLoading && library.total > 0 && <span className="dd-count"> ({library.total})</span>}
                    </span>
                  ) : (
                    <nav className="dd-path" aria-label="תיקיות">
                      {breadcrumb.length === 0
                        ? <span className="dd-path-current">{ROOT_LABEL}</span>
                        : pathLink(null, ROOT_LABEL)}
                      {breadcrumb.map((folder, i) => (
                        <span key={folder._id}>
                          <span className="dd-path-sep">/</span>
                          {i === breadcrumb.length - 1
                            ? <span className="dd-path-current">{folder.name}</span>
                            : pathLink(folder._id, folder.name)}
                        </span>
                      ))}
                    </nav>
                  )}
                  {canEdit && !isSearching && (
                    <button type="button" className="dd-new-folder" onClick={() => setNameDialog({ mode: 'newFolder' })}>
                      <FontAwesomeIcon icon={faFolderPlus} /> תיקייה חדשה
                    </button>
                  )}
                </div>
                {/* The open folder's description in full; its tile shows at most 150 characters. */}
                {!isSearching && openFolderDescription && (
                  <p className="dd-open-folder-description" data-testid="open-folder-description">{openFolderDescription}</p>
                )}
                {canEdit && allFolders.length > 0 && (
                  <p className="dd-hint">אפשר לגרור מסמכים ותיקיות אל תיקייה, או אל נתיב התיקיות שלמעלה</p>
                )}

                <UploadErrors messages={actionError ? [actionError] : []} onDismiss={() => setActionError('')} />

                <div className="protocols-list">
                  {isLoading && [1, 2, 3].map(i => <SkeletonCard key={i} />)}

                  {!isLoading && loadError && <p className="dd-message">{loadError}</p>}

                  {!isLoading && !loadError && library.folders.length > 0 && (
                    <div className="dd-folder-grid">
                      {library.folders.map(renderFolder)}
                    </div>
                  )}

                  {!isLoading && !loadError && library.documents.length > 0 && (
                    <div className="entities-list">
                      {library.documents.map(renderDocument)}
                    </div>
                  )}

                  {!isLoading && !loadError && nothingVisible && (isSearching || !libraryEmpty) && (
                    <p className="dd-message">{isSearching ? 'לא נמצאו מסמכים' : 'התיקייה ריקה'}</p>
                  )}

                  {!isLoading && !loadError && libraryEmpty && !isSearching && (
                    <div className="dd-empty">
                      <FaFolderOpen size={60} className="dd-empty-icon" />
                      <p>כאן ניתן לשמור ולשתף את מסמכי המחלקה - מצגות, הדרכות, נהלים ועוד - ולצפות בהם מהמחשב, הטאבלט או הסמארטפון.</p>
                      {canEdit && (
                        <button type="button" className="dd-empty-button" onClick={() => setUploadOpen(true)}>
                          העלאת מסמכים
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {!loadError && library.pages > 1 && (
                  <div className="pagination-container dd-pagination">
                    <Pagination
                      count={library.pages}
                      page={Math.min(page, library.pages)}
                      onChange={(_, value) => {
                        updateParams({ page: value > 1 ? String(value) : null });
                        window.scrollTo({ top: 0 });
                      }}
                      color="primary"
                    />
                  </div>
                )}
              </>
            )}

            <UploadDocumentsDialog
              open={uploadOpen}
              departmentId={departmentId}
              folderOptions={destinationOptions}
              initialFolder={openFolderId}
              onClose={() => setUploadOpen(false)}
              onUploaded={() => {
                setUploadOpen(false);
                reload();
              }}
            />

            <NameDialog
              open={!!nameDialog}
              {...nameDialogProps}
              onSubmit={handleNameSubmit}
              onClose={() => setNameDialog(null)}
            />

            <DocumentDetailsDialog
              open={!!detailsDoc}
              document={detailsDoc}
              folderPath={detailsDoc ? folderPathLabel(allFolders, detailsDoc.folder) : ''}
              onClose={() => setDetailsDoc(null)}
            />

            <DeleteConfirmationDialog
              open={!!toDelete}
              onClose={() => setToDelete(null)}
              onConfirm={handleDelete}
              title={toDelete?.kind === 'folder'
                ? `האם למחוק את התיקייה "${toDelete?.item?.name}"?`
                : `האם למחוק את המסמך "${toDelete?.item?.name}"?`}
              showError={!!deleteError}
              errorMessage={deleteError}
              onCloseError={() => setDeleteError('')}
            />
          </ThemeProvider>
        </CacheProvider>

        {canEdit && (
          <FloatingAddButton onClick={() => setUploadOpen(true)} entityName="מסמך" dataLoaded={!isLoading} />
        )}
      </div>
    </CommonLayout>
  );
}

export default DepartmentDocuments;
